import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ALL_PRESETS,
  computeRelativeDateRange,
  RELATIVE_DATE_LABELS,
  type RelativeDatePreset,
} from "@/lib/date-utils";
import type { FilterOperator } from "@/lib/filters";
import { cn } from "@/lib/utils";

export interface OperatorSelectProps {
  operators: FilterOperator[];
  value: FilterOperator;
  onChange: (op: FilterOperator) => void;
  className?: string;
  /** If true, shows relative date presets in the dropdown */
  showRelativeDates?: boolean;
  /** Called when a relative date preset is selected */
  onRelativeDateSelect?: (
    range: { from: string; to: string },
    preset: RelativeDatePreset,
  ) => void;
  /** Currently active relative date preset (for display) */
  activePreset?: RelativeDatePreset | null;
}

const OP_LABELS: Record<FilterOperator, string> = {
  IN: "one of",
  NOT_IN: "not one of",
  EQ: "=",
  NEQ: "≠",
  GT: ">",
  GTE: "≥",
  LT: "<",
  LTE: "≤",
  BETWEEN: "between",
  LIKE: "contains",
  ILIKE: "contains (insensitive)",
  STARTS_WITH: "starts with",
  ENDS_WITH: "ends with",
  IS_NULL: "is null",
  IS_NOT_NULL: "is not null",
};

/** Relative date presets to show in the dropdown (reuse from date-utils) */
const DATE_PRESETS = ALL_PRESETS;

export function OperatorSelect({
  operators,
  value,
  onChange,
  className,
  showRelativeDates = false,
  onRelativeDateSelect,
  activePreset,
}: OperatorSelectProps) {
  const handleValueChange = (selectedValue: string) => {
    // Check if it's a relative date preset
    if (
      showRelativeDates &&
      DATE_PRESETS.includes(selectedValue as RelativeDatePreset)
    ) {
      const preset = selectedValue as RelativeDatePreset;
      const range = computeRelativeDateRange(preset);
      onRelativeDateSelect?.(range, preset);
      return;
    }
    // Otherwise it's a regular operator
    onChange(selectedValue as FilterOperator);
  };

  // Use the preset as the Select value when active, otherwise the operator
  const selectValue = activePreset ?? value;

  // Check if current value has a matching SelectItem in the dropdown
  const hasMatchingItem =
    operators.includes(value) ||
    (showRelativeDates && activePreset && DATE_PRESETS.includes(activePreset));

  // Compute display value for fallback during field transitions
  const displayValue = activePreset
    ? RELATIVE_DATE_LABELS[activePreset]
    : value
      ? (OP_LABELS[value] ?? value)
      : "Op";

  return (
    <Select value={selectValue} onValueChange={handleValueChange}>
      <SelectTrigger className={cn("w-36", className)}>
        {hasMatchingItem ? (
          <SelectValue placeholder="Op" />
        ) : (
          <span className="truncate">{displayValue}</span>
        )}
      </SelectTrigger>
      <SelectContent>
        {showRelativeDates && (
          <>
            <SelectGroup>
              <SelectLabel>Relative</SelectLabel>
              {DATE_PRESETS.map((preset) => (
                <SelectItem
                  key={preset}
                  value={preset}
                  aria-label={`Relative date: ${RELATIVE_DATE_LABELS[preset]}`}
                >
                  {RELATIVE_DATE_LABELS[preset]}
                </SelectItem>
              ))}
            </SelectGroup>
            <SelectSeparator />
          </>
        )}
        <SelectGroup>
          {showRelativeDates && <SelectLabel>Operators</SelectLabel>}
          {operators.map((op) => (
            <SelectItem key={op} value={op}>
              {OP_LABELS[op] ?? op}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
